import React, { useState } from "react";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { AccountLogo } from "../AccountLogo";
import { AlsoReach, Chips, CompetitorTag, RoleLine, WhyItMatters, posterLine } from "../HiringParts";
import SignalIcon from "../SignalIcon";
import { hiringOf, timeAgo } from "../signalMeta";

/**
 * A hiring intel tracker's table, in List Builder's type: Account, What they're building, Tech, Who to reach, When.
 * Clicking a row opens its roles underneath. A recruiter's or a peer's post names the persona to reach, with Find.
 */
// "its Data Platform" reads as a column value: "Data Platform".
const building = text => {
  const t = String(text || "").replace(/^(its|their)\s+/i, "");
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const HiringTable = ({ moves, onRead, empty }) => {
  const [open, setOpen] = useState("");
  const toggle = m => {
    onRead(m);
    setOpen(open === m.id ? "" : m.id);
  };
  const onKey = (ev, m) => {
    if (ev.target !== ev.currentTarget || (ev.key !== "Enter" && ev.key !== " ")) return;
    ev.preventDefault();
    toggle(m);
  };
  // The Find link and its people card sit inside the row: their clicks never toggle it.
  const stop = ev => ev.stopPropagation();
  return (
    <div className="st-table st-table--hiring">
      <div className="st-row st-row--head st-hrow">
        <span>Account</span><span>What they're building</span><span>Tech</span><span>Who to reach</span><span>When</span>
      </div>
      {moves.length === 0 && <div className="st-empty">{empty}</div>}
      {moves.map(m => {
        const h = hiringOf(m.item);
        const owner = h.owner;
        const poster = h.poster;
        const persona = !h.isCompetitor && (h.ownerIsRecruiter || h.ownerIsPeer) ? h.persona : "";
        const name = h.accountName || m.name;
        const expanded = open === m.id;
        return (
          <div key={m.id} className={`st-hblock ${expanded ? "is-open" : ""}`}>
            <div role="button" tabIndex={0} className={`st-row st-hrow ${m.unread ? "is-unread" : ""}`} aria-expanded={expanded} onClick={() => toggle(m)} onKeyDown={ev => onKey(ev, m)}>
              <span className="st-cell">
                <AccountLogo domain={m.domain} name={name} />
                <span className="st-stack">
                  <span className="st-name">{name}{h.isCompetitor && <CompetitorTag />}</span>
                  {h.roles.length > 0 && <span className="st-sub">{h.roles.length} role{h.roles.length === 1 ? "" : "s"}</span>}
                </span>
              </span>
              <span className="st-what">{building(h.initiative) || h.summary || "Hiring"}</span>
              <span className="st-cell"><Chips items={h.technologies} max={3} /></span>
              <span className="st-cell st-person">
                {owner ? (
                  <>
                    <ContactAvatar photo={owner.photoUrl} name={owner.name} />
                    <span className="st-stack">
                      <span className="st-name">{owner.name}</span>
                      {owner.title && <span className="st-sub">{owner.title}</span>}
                    </span>
                  </>
                ) : persona ? (
                  <span className="st-stack">
                    <span className="st-reach">
                      <span className="st-name">{persona}</span>
                      <span className="st-find" onClick={stop} onKeyDown={stop}>
                        <AlsoReach label="Find" persona={persona} domain={m.domain} accountId={m.entityId} accountName={name} />
                      </span>
                    </span>
                    {poster && <span className="st-sub">{posterLine({ ...poster, title: null }, true)}</span>}
                  </span>
                ) : poster ? (
                  <>
                    <ContactAvatar photo={poster.photoUrl} name={poster.name} />
                    <span className="st-stack">
                      <span className="st-sub">{poster.name}</span>
                      <span className="st-sub">Talent Acquisition</span>
                    </span>
                  </>
                ) : <span className="st-sub">Not named</span>}
              </span>
              <span className="st-when">{timeAgo(m.at) || "Just now"} <SignalIcon id="chevron" size={13} className="st-chev" /></span>
            </div>
            {expanded && (
              <div className="sg-hroles st-hroles">
                <WhyItMatters why={h.why} competitor={h.isCompetitor} />
                {!h.isCompetitor && <AlsoReach persona={h.persona} domain={m.domain} accountId={m.entityId} accountName={name} />}
                {h.roles.length ? h.roles.map((r, i) => <RoleLine key={`${r.title}-${i}`} role={r} />) : <div className="st-empty">No roles listed in this post.</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HiringTable;
